/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import style from './Institution.module.css';
import { Table, Input, Pagination, Space, Dropdown, Checkbox, Tag, message, Modal, Form } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { CiMenuKebab, CiSearch } from 'react-icons/ci';
import Button from '../../components/Basic/button';
import Header from '../../components/Header/header';
import { connect } from 'react-redux';
import * as Actions from '../../store/slices/institution/institution';
import { hasPermission } from '../../utils/premission';
import { PERMISSIONS } from '../../utils/constant/permissions';
import { useNavigate } from 'react-router-dom';

interface Api {
  Institution_Red: any;
  getAllInstitutions: () => any;
  getInstitutionById: (id: number) => any;
  createInstitution: (data: any) => any;
  updateInstitution: (id: number, data: any) => any;
  deleteInstitution: (id: number) => any;
}

const InstitutionPage: React.FC<Api> = ({
  Institution_Red,
  getAllInstitutions,
  getInstitutionById,
  createInstitution,
  updateInstitution,
  deleteInstitution,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(['institutionName', 'contactNo', 'emailAddress', 'status', 'createdAt', 'createdBy']);
  const [loadingText, setLoadingText] = useState<boolean>(true);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    getAllInstitutions();
  }, []);

  useEffect(() => {
    setLoadingText(true);
    setTimeout(() => setLoadingText(false), 1500);
  }, []);

  useEffect(() => {
    if (Institution_Red?.dataSingle?.[0]?.res?.status === '1004') {
      message.error(Institution_Red.dataSingle[0].res.message);
      localStorage.clear();
      navigate('/');
    }
  }, [Institution_Red, navigate]);

  const dataSource = (Array.isArray(Institution_Red?.data) ? Institution_Red.data : []).map((item: any) => ({
    key: item.id?.toString(),
    id: item.id,
    institutionName: item.institutionName || '-',
    contactNo: item.contactNo || '-',
    emailAddress: item.emailAddress || '-',
    status: item.status || '-',
    createdAt: item.createdAt ? item.createdAt.replace('T', ' ').split('.')[0] : '-',
    createdBy: item.createdBy != null ? String(item.createdBy) : '-',
  }));

  const filteredData = dataSource.filter((item: any) =>
    item.institutionName.toLowerCase().includes(searchText) ||
    item.emailAddress.toLowerCase().includes(searchText) ||
    item.contactNo.toLowerCase().includes(searchText)
  );

  const onSearch = (value: string) => setSearchText(value.toLowerCase());

  const handleCheckboxChange = (checked: boolean, column: string) => {
    setSelectedColumns(prev =>
      checked ? [...prev, column] : prev.filter(c => c !== column)
    );
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setSelectedId(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE' });
    setIsFormModalOpen(true);
  };

  const openEditModal = async (record: any) => {
    setIsEditMode(true);
    setSelectedId(record.id);
    setIsFormModalOpen(true);
    const result = await getInstitutionById(record.id);
    if (result?.success && result?.data) {
      form.setFieldsValue({
        institutionName: result.data.institutionName,
        contactNo: result.data.contactNo,
        emailAddress: result.data.emailAddress,
        status: result.data.status,
      });
    } else {
      form.setFieldsValue({
        institutionName: record.institutionName,
        contactNo: record.contactNo,
        emailAddress: record.emailAddress,
        status: record.status,
      });
    }
  };

  const openDeleteModal = (record: any) => {
    setSelectedId(record.id);
    setIsDeleteModalOpen(true);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setButtonLoading(true);
      const result = isEditMode && selectedId
        ? await updateInstitution(selectedId, values)
        : await createInstitution(values);

      if (result?.success) {
        message.success(result.message || `Institution ${isEditMode ? 'updated' : 'created'} successfully`);
        setIsFormModalOpen(false);
        form.resetFields();
        getAllInstitutions();
      } else {
        message.error(result?.message || 'Operation failed');
      }
    } catch (err: any) {
      if (err?.errorFields) return; // validation error
      message.error(err?.message || 'An error occurred');
    } finally {
      setButtonLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setButtonLoading(true);
    try {
      const result = await deleteInstitution(selectedId);
      if (result?.success) {
        message.success(result.message || 'Institution deleted successfully');
        setIsDeleteModalOpen(false);
        getAllInstitutions();
      } else {
        message.error(result?.message || 'Failed to delete');
      }
    } catch (err: any) {
      message.error(err?.message || 'An error occurred');
    } finally {
      setButtonLoading(false);
      setSelectedId(null);
    }
  };

  const actionMenu = (record: any) => (
    <Space direction="vertical" className={style.actions}>
      {hasPermission(PERMISSIONS.INSTITUTION_UPDATE) && (
        <div className={style.dropdownItem} onClick={() => openEditModal(record)}>Edit</div>
      )}
      {hasPermission(PERMISSIONS.INSTITUTION_DELETE) && (
        <div className={style.dropdownItem} onClick={() => openDeleteModal(record)}>Delete</div>
      )}
    </Space>
  );

  const allColumns = [
    {
      title: 'Institution Name',
      dataIndex: 'institutionName',
      key: 'institutionName',
      width: 200,
      sorter: (a: any, b: any) => a.institutionName.localeCompare(b.institutionName),
    },
    {
      title: 'Contact No',
      dataIndex: 'contactNo',
      key: 'contactNo',
      width: 140,
    },
    {
      title: 'Email Address',
      dataIndex: 'emailAddress',
      key: 'emailAddress',
      width: 200,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const isActive = status?.toLowerCase() === 'active';
        return <Tag color={isActive ? 'green' : 'orange'}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (val: string) => val || '-',
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right' as const,
      width: 60,
      render: (_: any, record: any) => (
        <Dropdown overlay={() => actionMenu(record)} trigger={['click']} placement="bottomRight">
          <CiMenuKebab className={style.actionIcon} />
        </Dropdown>
      ),
    },
  ];

  const filteredColumns = allColumns.filter(col => selectedColumns.includes(col.key) || col.key === 'action');

  const columnItems = [
    { label: 'Institution Name', key: 'institutionName' },
    { label: 'Contact No', key: 'contactNo' },
    { label: 'Email Address', key: 'emailAddress' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Created By', key: 'createdBy' },
  ].map(({ label, key }) => ({
    key,
    label: (
      <Checkbox
        checked={selectedColumns.includes(key)}
        onChange={(e) => handleCheckboxChange(e.target.checked, key)}
      >
        {label}
      </Checkbox>
    ),
  }));

  return (
    <>
      <Header />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Institution"
        open={isDeleteModalOpen}
        onOk={handleDelete}
        onCancel={() => { setIsDeleteModalOpen(false); setSelectedId(null); }}
        okText="Delete"
        cancelText="Cancel"
        confirmLoading={buttonLoading}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this institution?</p>
      </Modal>

      {/* Create / Edit Modal */}
      <Modal
        title={isEditMode ? 'Edit Institution' : 'Create Institution'}
        open={isFormModalOpen}
        onOk={handleFormSubmit}
        onCancel={() => { setIsFormModalOpen(false); form.resetFields(); }}
        okText={isEditMode ? 'Update' : 'Create'}
        cancelText="Cancel"
        confirmLoading={buttonLoading}
        okButtonProps={{ style: { backgroundColor: '#095179', color: '#fff' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="institutionName" label="Institution Name" rules={[{ required: true, message: 'Institution name is required' }]}>
            <Input placeholder="Enter institution name" />
          </Form.Item>
          <Form.Item
            name="contactNo"
            label="Contact No"
            rules={[
              { pattern: /^[0-9+\-\s()]{7,12}$/, message: 'Enter a valid contact number (7-12 digits)' },
            ]}
          >
            <Input placeholder="Enter contact number" maxLength={15} />
          </Form.Item>
          <Form.Item name="emailAddress" label="Email Address" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Enter a valid email address' }]}>
            <Input placeholder="Enter email address" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
            <select style={{ width: '100%', padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9' }}>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
          </Form.Item>
        </Form>
      </Modal>

      <div className={style.Route}>
        <h5>Institution Management</h5>
        <div className={style.btns}>
          {hasPermission(PERMISSIONS.INSTITUTION_CREATE) && (
            <Button Text="Create Institution" buttonClass={style.createBtn} onClick={openCreateModal} Disable={loadingText} />
           )} 
        </div>
      </div>

      <div className={style.container}>
        <Space className={style.SearchTable}>
          <div className={style.SearchBar}>
            <Input
              placeholder="Search..."
              prefix={<CiSearch size={20} />}
              onChange={(e) => onSearch(e.target.value)}
              style={{ width: 200 }}
              className={style.inputSearch}
            />
            <Dropdown menu={{ items: columnItems }} trigger={['click']}>
              <SettingOutlined className={style.tableSettings} />
            </Dropdown>
          </div>
        </Space>
        <Table
          loading={Institution_Red?.loading || loadingText}
          columns={filteredColumns}
          dataSource={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
          pagination={false}
          rowSelection={{ type: 'checkbox' }}
          size="small"
        />
        <Pagination
          align="end"
          showTotal={(total) => `Total ${total} items`}
          current={currentPage}
          total={filteredData.length}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50]}
          showSizeChanger
          onChange={(page, newSize) => {
            setCurrentPage(page);
            if (newSize !== pageSize) { setPageSize(newSize); setCurrentPage(1); }
          }}
          style={{ marginTop: 16 }}
        />
      </div>
    </>
  );
};

function mapStateToProps(state: any) {
  return { Institution_Red: state.Institution_Red };
}

export default connect(mapStateToProps, Actions)(InstitutionPage);
